import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { ScanCTA } from "@/components/ScanCTA";
import { Plus, MoreVertical, Star, AlertTriangle, Unlink, Sparkles } from "lucide-react";
import { linkChipLocal, updateChipLocal, unlinkChipLocal } from "@/lib/repo";
import { toast } from "sonner";

const CHIP_STATUS = {
  active:  { label: "Activo",   variant: "present" },
  damaged: { label: "Dañado",   variant: "ochre"   },
  lost:    { label: "Perdido",  variant: "missing" },
  backup:  { label: "Respaldo", variant: "warm"    },
};

const REASON_LABEL = {
  original:    "Original",
  replacement: "Reemplazo",
  backup:      "Respaldo",
  recovered:   "Recuperado",
};

export function AnimalChipsCard({ animal, canEdit, onChange }) {
  const chips = animal?.chips || [];
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    rfid: "",
    reason: "replacement",
    make_primary: true,
    previous_status: "damaged",
    notes: "",
  });

  const reset = () => setForm({
    rfid: "", reason: "replacement", make_primary: true,
    previous_status: "damaged", notes: "",
  });

  const submit = async () => {
    if (!form.rfid || form.rfid.length < 6) {
      toast.error("Ingresá un RFID válido (mínimo 6 caracteres)");
      return;
    }
    setBusy(true);
    try {
      const updated = await linkChipLocal(animal.id, {
        rfid: form.rfid.trim(),
        reason: form.reason,
        make_primary: form.make_primary,
        previous_status: form.make_primary ? form.previous_status : null,
        notes: form.notes || null,
      });
      toast.success("Chip vinculado correctamente");
      setSheetOpen(false);
      reset();
      onChange?.(updated);
    } catch (e) {
      toast.error(e?.userMessage || "No pude vincular el chip");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (rfid, status) => {
    try {
      const updated = await updateChipLocal(animal.id, rfid, { status });
      toast.success("Chip actualizado");
      onChange?.(updated);
    } catch (e) {
      toast.error(e?.userMessage || "No pude actualizar el chip");
    }
  };

  const makePrimary = async (rfid) => {
    try {
      const updated = await updateChipLocal(animal.id, rfid, { make_primary: true });
      toast.success("Chip marcado como principal");
      onChange?.(updated);
    } catch (e) {
      toast.error(e?.userMessage || "No pude marcar el chip");
    }
  };

  const unlink = async (rfid) => {
    if (!window.confirm(`¿Desvincular el chip ${rfid} de este animal? Se perderá el historial de lecturas con ese chip.`)) return;
    try {
      const updated = await unlinkChipLocal(animal.id, rfid);
      toast.success("Chip desvinculado");
      onChange?.(updated);
    } catch (e) {
      toast.error(e?.userMessage || "No pude desvincular el chip");
    }
  };

  return (
    <Card data-testid="animal-chips-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Trazabilidad</div>
          <CardTitle className="text-[17px] mt-0.5">Chips vinculados</CardTitle>
          <p className="text-[12px] text-muted-foreground mt-1">
            Un animal puede tener varios chips. Si se lee cualquiera, se reconoce como el mismo animal.
          </p>
        </div>
        {canEdit && (
          <Sheet open={sheetOpen} onOpenChange={(o) => { setSheetOpen(o); if (!o) reset(); }}>
            <SheetTrigger asChild>
              <Button size="sm" data-testid="chip-add-button"><Plus size={14}/>Agregar chip</Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-[var(--radius)] max-h-[92vh] overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="font-heading">Vincular nuevo chip</SheetTitle>
                <SheetDescription className="text-[12.5px]">
                  Agregá un chip de reemplazo, respaldo o recuperado. Solo el dueño puede vincular o desvincular chips.
                </SheetDescription>
              </SheetHeader>
              <div className="py-3 space-y-4">
                <div>
                  <Label className="text-[12px] text-muted-foreground">Código RFID</Label>
                  <Input
                    data-testid="chip-add-rfid"
                    value={form.rfid}
                    onChange={(e)=>setForm({ ...form, rfid: e.target.value.replace(/[^A-Za-z0-9-]/g, "").toUpperCase() })}
                    className="h-11 font-mono tabular-nums tracking-[0.04em]"
                    placeholder="900164000000000"
                    maxLength={30}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Si escaneás con el lector, el código se completa automáticamente.
                  </p>
                </div>
                <ScanCTA testId="chip-add-scan-button" onChip={(c)=>setForm((f)=>({ ...f, rfid: c }))} />

                <div>
                  <Label className="text-[12px] text-muted-foreground">Motivo</Label>
                  <Select value={form.reason} onValueChange={(v)=>setForm({ ...form, reason: v })}>
                    <SelectTrigger data-testid="chip-add-reason" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="replacement">Reemplazo del chip principal</SelectItem>
                      <SelectItem value="backup">Chip de respaldo</SelectItem>
                      <SelectItem value="recovered">Chip recuperado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[12px] text-muted-foreground">¿Es el nuevo chip principal?</Label>
                  <RadioGroup
                    value={form.make_primary ? "yes" : "no"}
                    onValueChange={(v)=>setForm({ ...form, make_primary: v === "yes" })}
                    className="flex gap-3"
                    data-testid="chip-add-primary-group"
                  >
                    <Label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 cursor-pointer flex-1">
                      <RadioGroupItem value="yes" />
                      <span className="text-sm">Sí, reemplaza al actual</span>
                    </Label>
                    <Label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 cursor-pointer flex-1">
                      <RadioGroupItem value="no" />
                      <span className="text-sm">No, queda como secundario</span>
                    </Label>
                  </RadioGroup>
                </div>

                {form.make_primary && (
                  <div>
                    <Label className="text-[12px] text-muted-foreground">¿Qué pasa con el chip anterior?</Label>
                    <Select value={form.previous_status} onValueChange={(v)=>setForm({ ...form, previous_status: v })}>
                      <SelectTrigger data-testid="chip-add-previous-status" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="damaged">Marcar como dañado</SelectItem>
                        <SelectItem value="lost">Marcar como perdido</SelectItem>
                        <SelectItem value="backup">Quedar como respaldo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-[12px] text-muted-foreground">Notas (opcional)</Label>
                  <Textarea
                    data-testid="chip-add-notes"
                    value={form.notes}
                    onChange={(e)=>setForm({ ...form, notes: e.target.value })}
                    rows={2}
                    placeholder="Ej: chip original se cayó, reemplazo en oreja derecha"
                  />
                </div>

                <Button
                  data-testid="chip-add-submit"
                  onClick={submit}
                  disabled={busy}
                  size="lg"
                  className="w-full"
                >
                  {busy ? "Vinculando…" : "Vincular chip"}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </CardHeader>
      <CardContent className="space-y-2.5">
        {chips.length === 0 && (
          <p className="text-[13px] text-muted-foreground italic">Este animal no tiene chips registrados.</p>
        )}
        {chips.map((chip) => {
          const meta = CHIP_STATUS[chip.status] || CHIP_STATUS.active;
          return (
            <div
              key={chip.rfid}
              data-testid={`chip-row-${chip.rfid}`}
              className="flex items-center gap-3 rounded-md border border-border bg-background/40 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[13px] tabular-nums tracking-[0.04em] text-foreground">
                    {chip.rfid}
                  </span>
                  {chip.is_primary && (
                    <Badge variant="default" className="gap-1">
                      <Star size={10} className="fill-current"/>Principal
                    </Badge>
                  )}
                  <Badge variant={meta.variant}>{meta.label}</Badge>
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                    {REASON_LABEL[chip.reason] || chip.reason}
                  </Badge>
                </div>
                {chip.notes && (
                  <div className="text-[12px] text-muted-foreground mt-1 line-clamp-2">{chip.notes}</div>
                )}
                <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground mt-1">
                  Vinculado {new Date(chip.linked_at).toLocaleDateString("es-AR")}
                </div>
              </div>
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`chip-menu-${chip.rfid}`}>
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {!chip.is_primary && (
                      <DropdownMenuItem onClick={()=>makePrimary(chip.rfid)} data-testid={`chip-action-make-primary-${chip.rfid}`}>
                        <Star size={14}/> Marcar como principal
                      </DropdownMenuItem>
                    )}
                    {chip.status !== "active" && (
                      <DropdownMenuItem onClick={()=>setStatus(chip.rfid, "active")} data-testid={`chip-action-active-${chip.rfid}`}>
                        <Sparkles size={14}/> Marcar como activo
                      </DropdownMenuItem>
                    )}
                    {chip.status !== "damaged" && (
                      <DropdownMenuItem onClick={()=>setStatus(chip.rfid, "damaged")} data-testid={`chip-action-damaged-${chip.rfid}`}>
                        <AlertTriangle size={14}/> Marcar como dañado
                      </DropdownMenuItem>
                    )}
                    {chip.status !== "lost" && (
                      <DropdownMenuItem onClick={()=>setStatus(chip.rfid, "lost")} data-testid={`chip-action-lost-${chip.rfid}`}>
                        <AlertTriangle size={14}/> Marcar como perdido
                      </DropdownMenuItem>
                    )}
                    {chip.status !== "backup" && (
                      <DropdownMenuItem onClick={()=>setStatus(chip.rfid, "backup")} data-testid={`chip-action-backup-${chip.rfid}`}>
                        <Sparkles size={14}/> Marcar como respaldo
                      </DropdownMenuItem>
                    )}
                    {chips.length > 1 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={()=>unlink(chip.rfid)}
                          data-testid={`chip-action-unlink-${chip.rfid}`}
                        >
                          <Unlink size={14}/> Desvincular del animal
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })}
        {!canEdit && (
          <p className="text-[11.5px] text-muted-foreground italic mt-1">
            Solo el dueño puede vincular o desvincular chips.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
